import { useAuth } from '@clerk/clerk-react';
import { Activity, Download } from 'lucide-react';
import { useState } from 'react';
import { API_BASE_URL } from '../api/client.js';
import { useMe } from '../api/useMe.js';
import { type UtilisationStatus, useUtilisationReport } from '../api/useReports.js';
import { useTeamsList } from '../api/useTeams.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';

const STATUS_META: Record<UtilisationStatus, { label: string; classes: string }> = {
  // Red, not amber: the worker is ON SHIFT and invisible right now.
  tracking_gap: { label: 'Tracking gap', classes: 'bg-red-100 text-red-800' },
  healthy: { label: 'Healthy', classes: 'bg-emerald-100 text-emerald-800' },
  update_needed: { label: 'Update needed', classes: 'bg-amber-100 text-amber-800' },
  no_background: { label: 'No background', classes: 'bg-amber-100 text-amber-800' },
  no_push: { label: 'No push', classes: 'bg-amber-100 text-amber-800' },
  stale: { label: 'Stale', classes: 'bg-red-100 text-red-800' },
};

function formatRelativeOrDash(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} d ago`;
}

export default function UtilisationReport() {
  const me = useMe();
  const teams = useTeamsList();
  const allTeams = teams.data?.teams ?? [];
  const [teamId, setTeamId] = useState<string>('');
  const [windowDays, setWindowDays] = useState<number>(7);
  const report = useUtilisationReport(
    { teamIds: teamId || undefined, windowDays },
    Boolean(me.data),
  );
  const rows = report.data?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <span className="eyebrow">Reports</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            App utilisation
          </h1>
          <p className="text-sm text-ink-faint">
            Per-worker snapshot of how the mobile app is being used. Status flag rolls up last-seen
            + build + background + push signals so you can catch outdated installs and inactive
            workers in one pass.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <Picker label="Team" value={teamId} onChange={setTeamId}>
            <option value="">
              {me.data?.capabilities?.monitor || me.data?.capabilities?.admin
                ? 'All teams'
                : 'My teams'}
            </option>
            {allTeams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Picker>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-ink-faint">Window</label>
            <select
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            >
              <option value={1}>Last 24 h</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>
          <CsvDownloadButton windowDays={windowDays} teamIds={teamId || undefined} />
        </div>
      </div>

      {report.isError && (
        <Banner kind="error">Failed to load report: {(report.error as Error).message}</Banner>
      )}
      {report.isLoading && (
        <div className="panel p-8 text-center text-sm text-ink-faint">Loading…</div>
      )}
      {!report.isLoading && rows.length === 0 && !report.isError && (
        <EmptyState
          icon={Activity}
          title="No workers in scope"
          description="No workers were found for the selected team."
        />
      )}

      {rows.length > 0 && (
        <div className="panel">
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Worker</Th>
                <Th>Last seen</Th>
                <Th>Build</Th>
                <Th>Background</Th>
                <Th>Push</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {rows.map((r) => {
                const meta = STATUS_META[r.status];
                return (
                  <tr key={r.user_id} className="hover:bg-surface-3">
                    <Td>
                      <div className="text-sm font-medium text-ink">{r.display_name}</div>
                      {r.job_title && <div className="text-xs text-ink-faint">{r.job_title}</div>}
                    </Td>
                    <Td className="text-sm text-ink-dim">{formatRelativeOrDash(r.last_seen_at)}</Td>
                    <Td className="text-sm">
                      {r.has_device_api_token ? (
                        <span className="text-emerald-700">
                          ✅ {r.device_api_token_label ?? 'modern build'}
                        </span>
                      ) : (
                        <span className="text-amber-700">⚠ old build</span>
                      )}
                    </Td>
                    <Td className="text-sm">
                      {r.latest_background_at ? (
                        <span className="text-emerald-700">
                          ✅ {formatRelativeOrDash(r.latest_background_at)}
                        </span>
                      ) : (
                        <span className="text-amber-700">— never</span>
                      )}
                    </Td>
                    <Td className="text-sm">
                      {r.has_push_token ? (
                        <span className="text-emerald-700">✅</span>
                      ) : (
                        <span className="text-amber-700">— none</span>
                      )}
                    </Td>
                    <Td>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                          meta.classes,
                        )}
                      >
                        {meta.label}
                      </span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Picker({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-ink-faint">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-[12rem] rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
      >
        {children}
      </select>
    </div>
  );
}

function Banner({ kind, children }: { kind: 'error'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'mb-4 rounded-md border px-4 py-3 text-sm',
        kind === 'error' && 'border-red-200 bg-red-50 text-red-800',
      )}
    >
      {children}
    </div>
  );
}

function CsvDownloadButton({
  windowDays,
  teamIds,
}: {
  windowDays: number;
  teamIds?: string;
}) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);
  async function onClick() {
    setBusy(true);
    try {
      const token = await getToken();
      const sp = new URLSearchParams({ window_days: String(windowDays) });
      if (teamIds) sp.set('team_ids', teamIds);
      const res = await fetch(`${API_BASE_URL}/reports/utilisation.csv?${sp.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utilisation-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line-strong bg-white px-3 text-sm font-medium text-ink-dim transition hover:bg-surface-3 disabled:opacity-50"
      title="Download as CSV"
    >
      <Download className="h-3.5 w-3.5" />
      {busy ? 'Preparing…' : 'CSV'}
    </button>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}
